import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useNotification } from "providers/NotificationProvider";

import userServices from "services/userServices";
import constants from "constants";
import UserInfo from "./UserInfo";
import UserProfileHeader from "./UserProfileHeader";
import CountriesCodes from "assets/data/country_phone.json";
import { useAuth } from "context/AuthContext";
import {Container, Row, Col, Card, CardHeader } from "reactstrap";
import UserReviewsModal from "modals/UserReviewsModal";
import { createUserActionHandlers } from "helpers/userActionsHelper";
import UpdateUserModal from "modals/UpdateUserModal";
import DeleteModal from "modals/DeleteModal";
import { useTranslation } from "context/TranslationProvider";
import UserActivityLogsTable from "../components/UserActivityLogsTable";
import UserFlaggedHistoryTable from "../components/UserFlaggedHistoryTable";

const allowedRoles = [
  "super_admin",
  "admin",
  "moderator",
  "support",
  // "marketing"
];

const UserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  const [localUserData, setLocalUserData] = useState(null);
  const [country, setCountry] = useState({ code: "", label: "Select country" });
  const [countryPhoneCode, setCountryPhoneCode] = useState(null);
  const [pNum, setPNum] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [userReviews, setUserReviews] = useState([]);
  const { notify } = useNotification();

  const fetchErrorText = t("page.users.toast.fetchError", "Failed to fetch user data.");
  const adminOnlyText = t("page.users.toast.adminOnly", "Only admins can access this page.");

  const [itemToDelete, setItemToDelete] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  // Update User Modal
  const [updateUserOpen, setUpdateUserOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [statusLoadingId, setStatusLoadingId] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await userServices.getUserDetails(userId);
        const userData = res?.data?.data || res?.data || res;
        if (userData) {
          userData.name = (userData.firstName || "") + " " + (userData.lastName || "");

          setLocalUserData(userData);
          setCountry(userData.country || {});

          if (userData.phoneNumber) {
            // Normalize phone (remove spaces & dashes)
            const normalizedPhone = userData.phoneNumber.replace(/[\s-]/g, "");

            // Find best match (longest prefix)
            const foundPhoneCountry = CountriesCodes.reduce((bestMatch, current) => {
              const dial = current?.dial_code?.replace(/[\s-]/g, "") || current?.phone?.replace(/[\s-]/g, "");
              if (!dial) return bestMatch;

              if (!normalizedPhone.startsWith(dial)) return bestMatch;

              if (
                !bestMatch ||
                dial.length >
                  (bestMatch.dial_code?.replace(/[\s-]/g, "").length || bestMatch.phone?.replace(/[\s-]/g, "").length || 0)
              ) {
                return current;
              }

              return bestMatch;
            }, null);

            // Extract raw number
            const rawNum = foundPhoneCountry?.phone || foundPhoneCountry?.dial_code
              ? normalizedPhone
                  .slice(
                    (foundPhoneCountry.phone || foundPhoneCountry.dial_code).replace(/[\s-]/g, "").length
                  )
                  .trim()
              : userData.phoneNumber;

            setPNum(rawNum);
            setCountryPhoneCode(foundPhoneCountry || null);
          }

          setUserReviews(userData?.reviews || []);
        }
      } catch (error) {
        notify("error", fetchErrorText);
      }
    };
    if (user && allowedRoles.includes(user.role?.slug) && userId) {
      fetchUser();
    } else if (user && !allowedRoles.includes(user.role?.slug)) {
      notify("error", adminOnlyText);
      setTimeout(() => navigate("/"), 800);
    }
  }, [
    userId,
    user,
    navigate,
    notify,
    fetchErrorText,
    adminOnlyText
  ]);
      

  const profilePicUrl = localUserData?.profilePictureUrl
    ? `${constants.s3Bucket}/profile-pics/${localUserData.profilePictureUrl}`
    : null;
  
  const handleModalClose = () => {
    setOpenModal(false);
  };

  // DELETE
  const deleteRow = (item) => {
    setItemToDelete(item);
    setOpenDeleteModal(true);
  };

  const handleDeleteModalClose = () => {
    setOpenDeleteModal(false);
    setItemToDelete(null);
  };

  const deleteUser = () => {
    if (!itemToDelete?._id) return;

    setStatusLoadingId(itemToDelete._id);
    userServices
      .deleteUser({ deletedListingId: itemToDelete._id })
      .then((res) => {
        if (res?.success) {
          notify("success", t(res?.messageKey || "page.users.toast.deleteSuccess", res?.message || "User deleted successfully"));
          navigate("/admin/users");
        } else {
          notify("error", t(res?.messageKey || res?.error || "page.users.toast.fetchError", res?.message || "Failed to delete user"));
        }
      })
      .catch((err) => {
        const errPayload = err?.response?.data || {};
        notify("error", t(errPayload?.messageKey || errPayload?.error || "page.users.toast.fetchError", errPayload?.message || "Failed to delete user"));
      })
      .finally(() => {
        setStatusLoadingId(null);
        handleDeleteModalClose();
      });
  };

  // RESET PASSWORD
  const updatePassword = (item) => {
    if (!item?.email) return;

    setStatusLoadingId(item._id);
    userServices
      .forgotPasswordEmail(item.email, user._id)
      .then((res) => {
        if (res?.success) notify("success", t(res?.messageKey || "auth.forgot-password.resetLinkMessage", res?.message || t("auth.forgot-password.resetLinkMessage", "Reset password link sent to email.")));
        else notify("error", t(res?.messageKey || res?.error || "toast.somethingWentWrong", res?.message || t("toast.somethingWentWrong", "Something went wrong while creating the listing")));
      })
      .catch((err) => {
        const errPayload = err?.response?.data || {};
        notify("error", t(errPayload?.messageKey || errPayload?.error || "toast.unexpectedError", errPayload?.message || t("toast.unexpectedError", "Unexpected error occurred.")));
      })
      .finally(() => setStatusLoadingId(null));
  };

  // EDIT
  const handleUpdateUserOpen = (user) => {
    setSelectedUser(localUserData);
    setUpdateUserOpen(true);
  };

  const handleUpdateUserClose = () => {
    setUpdateUserOpen(false);
    setSelectedUser(null);
  };

  const updateUser = async (updatedData) => {
    if (!selectedUser) return;
    try {
      const parts = updatedData.name.trim().split(" ");
      const payload = {
        firstName: parts[0],
        lastName: parts.slice(1).join(" "),
        email: updatedData.email?.trim().toLowerCase(),
        phoneNumber: updatedData.phoneNumber,
        country: updatedData.country,
        homeAddress: updatedData.homeAddress,
        uploadedIdType: updatedData.uploadedIdType,
        uploadedIdNumber: updatedData.uploadedIdNumber,
      };
      const res = await userServices.updateUserDetails(selectedUser._id, payload);
      const resBody = res?.data || res;
      const dataObj = resBody?.data || resBody;

      if (resBody?.error) {
        const errorKey = resBody.error;
        if (errorKey === "auth.signup.emailAlreadyUsed") {
          return { error: errorKey };
        }
        notify("error", t(resBody?.messageKey || errorKey, resBody?.message || t("toast.unexpectedError", "Unexpected error occurred.")));
        return { error: errorKey };
      }

      if (resBody?.success || dataObj?._id) {
        notify("success", t(resBody?.messageKey || "toast.userUpdated", resBody?.message || t("toast.userUpdated", "User updated successfully")));
        setLocalUserData((prev) => ({ ...prev, ...updatedData }));

        const storedPhone = updatedData.phoneNumber || "";
        const normalizedPhone = storedPhone.replace(/[^\d]/g, "");
        let foundPhoneCountry = null;
        let longestCodeLength = 0;

        CountriesCodes.forEach((c) => {
          if (!c?.phone) return;
          const normalizedCode = String(c.phone).replace(/[^\d]/g, "");
          if (
            normalizedCode &&
            normalizedPhone.startsWith(normalizedCode) &&
            normalizedCode.length > longestCodeLength
          ) {
            longestCodeLength = normalizedCode.length;
            foundPhoneCountry = c;
          }
        });

        const rawNum = foundPhoneCountry
          ? normalizedPhone.slice(longestCodeLength)
          : normalizedPhone;

        setPNum(rawNum);
        setCountryPhoneCode(foundPhoneCountry || null);
        setCountry(updatedData.country || {});
        handleUpdateUserClose();
        return { success: true };
      }

      notify("error", t("toast.unexpectedError", "Unexpected error occurred."));
      return { error: "unknown_error" };

    } catch (err) {
      console.error(err);
      const backendError = err?.response?.data?.error;
      const backendMsgKey = err?.response?.data?.messageKey;
      const backendMessage = err?.response?.data?.message;
      if (backendError === "auth.signup.emailAlreadyUsed") {
        return { error: backendError };
      }
      notify("error", t(backendMsgKey || backendError || "toast.unexpectedError", backendMessage || t("toast.unexpectedError", "Unexpected error occurred.")));
      return { error: backendError || "toast.unexpectedError" };
    }
  };

  const handleToggleAccount = async (
    row,
    isActive,
    reasonText = null,
    selectedReason = null,
    onSuccess
  ) => {
    const newStatus = isActive ? "active" : "suspended";

    setStatusLoadingId(row._id);

    try {
      const res = await userServices.updateUserDetails(row._id, {
        accountStatus: newStatus,
        adminRemarks: reasonText,
        selectedReason,
      });

      const dataObj = res?.data?.data || res?.data || res;
      if (res?.data?.success || dataObj?._id) {
        notify("success", t(res?.data?.messageKey || "page.users.toast.statusUpdated", res?.data?.message || t("page.users.toast.statusUpdated", "Status updated successfully")));

        setLocalUserData((prev) => ({ ...prev, accountStatus: newStatus }));

        if (onSuccess) onSuccess();
      } else {
        notify("error", t(res?.data?.messageKey || res?.data?.error || "page.users.toast.somethingWentWrong", res?.data?.message || t("page.users.toast.somethingWentWrong", "Something went wrong")));
      }
    } catch (err) {
      console.error(err);
      notify("error", t("page.users.toast.unexpectedError", "Unexpected error occurred."));
    } finally {
      setStatusLoadingId(null);
    }
  };

  const userActions = createUserActionHandlers({
    onDelete: deleteRow,
    onResetPassword: updatePassword,
    onEditUser: handleUpdateUserOpen,
  });
  
  return (
    <>
    <Container fluid className="user-details-wrapper mt-4">
    <Row className="justify-content-center">
        <Col lg="12">
        <Card className="shadow border-0">
            <CardHeader className="bg-transparent">
            <UserProfileHeader
                localUserData={localUserData}
                profilePicUrl={profilePicUrl}
                setOpenModal = {setOpenModal}
                setLocalUserData={setLocalUserData}
                userActions={userActions}
                handleToggleAccount={handleToggleAccount}
                statusLoadingId={statusLoadingId}
                setStatusLoadingId={setStatusLoadingId}
            />
            </CardHeader>
        </Card>

            <UserInfo
                localUserData={localUserData}
                country={country}
                pNum={pNum}
                countryPhoneCode={countryPhoneCode}
                onEdit={() => handleUpdateUserOpen(localUserData)}
            />
            {/* Flagged History Table */}
            <UserFlaggedHistoryTable userId={userId} />

            {/* Activity Logs Table */}
            <UserActivityLogsTable userId={userId} />
          </Col>
    </Row>
    <UserReviewsModal
      isOpen={openModal}
      toggle={handleModalClose}
      reviews={userReviews || []} 
    />

    
    </Container>

    <DeleteModal
        isOpen={openDeleteModal}
        toggle={handleDeleteModalClose}
        itemName="user"
        onDelete={deleteUser}
        loading={statusLoadingId !== null}
      />

      {selectedUser && (<UpdateUserModal
        user={selectedUser}
        open={updateUserOpen}
        onClose={handleUpdateUserClose}
        onSave={updateUser}
      />
      )}
    </>
  );
};

export default UserDetails;
